import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { adminApi } from '../services/api';
import { toast } from 'sonner';
import { useAdminSocket } from '../hooks/useAdminSocket';
import { GoogleMap, useJsApiLoader, OverlayView, Polyline } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, isGoogleMapsConfigured } from '../config/googleMaps';
import GoogleMapsLoadError from '../components/GoogleMapsLoadError';

const MAP_STYLES = [
  { featureType: 'all',       elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'landscape', elementType: 'geometry',         stylers: [{ color: '#0f172a' }] },
  { featureType: 'road',      elementType: 'geometry.fill',    stylers: [{ color: '#1e293b' }] },
  { featureType: 'road',      elementType: 'geometry.stroke',  stylers: [{ visibility: 'off' }] },
  { featureType: 'water',     elementType: 'geometry',         stylers: [{ color: '#0c1a2e' }] },
  { featureType: 'poi',                                        stylers: [{ visibility: 'off' }] },
  { featureType: 'transit',                                    stylers: [{ visibility: 'off' }] },
];

const TASHKENT = { lat: 41.2995, lng: 69.2401 };

/* ─── Colours per driver (cycling) ─────────────────────── */
const PALETTE = ['#f43f5e','#3b82f6','#10b981','#f59e0b','#8b5cf6','#06b6d4'];
const driverColor = (idx) => PALETTE[idx % PALETTE.length];

/* ─── Inline SVG truck marker (coloured) ───────────────── */
const TruckMarker = ({ color = '#f43f5e', label = '', isFraud = false }) => (
  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Pulse ring */}
    <div style={{
      position: 'absolute', top: '-10px', left: '-10px', right: '-10px', bottom: '-10px',
      borderRadius: '50%', background: `${color}22`,
      animation: 'ping 1.8s cubic-bezier(0,0,0.2,1) infinite',
    }} />
    {/* Label chip */}
    {label && (
      <div style={{
        position: 'absolute', top: '-28px', left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
        color: 'white', fontSize: '8px', fontWeight: 900, fontStyle: 'italic',
        padding: '2px 8px', borderRadius: '8px', whiteSpace: 'nowrap',
        border: `1px solid ${color}55`, letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        {label}
      </div>
    )}
    {/* Fraud badge */}
    {isFraud && (
      <div style={{
        position: 'absolute', top: '-46px', left: '50%', transform: 'translateX(-50%)',
        background: '#ef4444', color: 'white', fontSize: '7px', fontWeight: 900,
        padding: '1px 6px', borderRadius: '6px', whiteSpace: 'nowrap',
        animation: 'pulse 1s ease-in-out infinite',
      }}>
        ⚠️ FRAUD
      </div>
    )}
    <svg width="40" height="40" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="22" fill={color} opacity="0.92" />
      <rect x="8"  y="18" width="18" height="10" rx="2" fill="white" opacity="0.95" />
      <rect x="26" y="20" width="10" height="8"  rx="2" fill="white" opacity="0.95" />
      <rect x="28" y="21.5" width="5" height="3" rx="1" fill={color} opacity="0.7" />
      <circle cx="14" cy="29" r="2.5" fill="rgba(0,0,0,0.4)" />
      <circle cx="14" cy="29" r="1"   fill="white" opacity="0.6" />
      <circle cx="31" cy="29" r="2.5" fill="rgba(0,0,0,0.4)" />
      <circle cx="31" cy="29" r="1"   fill="white" opacity="0.6" />
      <line x1="12" y1="21" x2="12" y2="27" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" />
      <line x1="16" y1="21" x2="16" y2="27" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" />
      <line x1="20" y1="21" x2="20" y2="27" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" />
    </svg>
  </div>
);

const MAX_TRAIL = 60;

function AdminDriverGoogleMap({
  activeRoutes,
  trails,
  fraudIds,
  selected,
  setSelected,
  mapCenter,
  mapOptions,
  mapRef,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-admin',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  if (loadError) return <GoogleMapsLoadError error={loadError} />;
  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={mapCenter}
      zoom={selected ? 15 : 12}
      options={mapOptions}
      onLoad={(m) => { mapRef.current = m; }}
    >
      {activeRoutes.map((route, idx) => {
        const lat = route.lastLocationLat ? parseFloat(route.lastLocationLat) : null;
        const lng = route.lastLocationLng ? parseFloat(route.lastLocationLng) : null;
        if (!lat || !lng) return null;

        const color   = driverColor(idx);
        const isFraud = route.vehicle && fraudIds.has(route.vehicle.id);
        const trail   = trails[route.id] || [];

        return (
          <React.Fragment key={route.id}>
            {trail.length > 1 && (
              <Polyline
                path={trail}
                options={{
                  strokeColor: color,
                  strokeOpacity: selected && selected !== route.id ? 0.2 : 0.6,
                  strokeWeight: selected === route.id ? 4 : 2,
                  geodesic: true,
                }}
              />
            )}
            <OverlayView
              position={{ lat, lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
            >
              <div
                style={{ transition: 'transform 0.8s cubic-bezier(0.4,0,0.2,1)', cursor: 'pointer' }}
                onClick={() => setSelected(selected === route.id ? null : route.id)}
              >
                <TruckMarker
                  color={color}
                  label={route.driver?.name?.split(' ')[0]}
                  isFraud={isFraud}
                />
              </div>
            </OverlayView>
          </React.Fragment>
        );
      })}
    </GoogleMap>
  );
}

const AdminDriverTracking = () => {
  const [activeRoutes, setActiveRoutes] = useState([]);
  const [trails, setTrails]             = useState({});   // routeId → [{lat,lng}]
  const [fraudIds, setFraudIds]         = useState(new Set());
  const [selected, setSelected]         = useState(null); // routeId
  const [loading, setLoading]           = useState(true);
  const mapRef    = useRef(null);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    styles: MAP_STYLES,
    gestureHandling: 'greedy',
  }), []);

  /* ─── Fetch routes ────────────────────────────────────── */
  const fetchRoutes = useCallback(async () => {
    try {
      const data = await adminApi.getActiveRoutes();
      const routes = data || [];
      setActiveRoutes(routes);

      // seed trails from last known location
      setTrails(prev => {
        const next = { ...prev };
        routes.forEach(r => {
          if (!next[r.id] && r.lastLocationLat) {
            next[r.id] = [{ lat: parseFloat(r.lastLocationLat), lng: parseFloat(r.lastLocationLng) }];
          }
        });
        return next;
      });
    } catch {
      toast.error('Faol yo\'nalishlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleLocationUpdate = useCallback((data) => {
    const pos = { lat: parseFloat(data.lat), lng: parseFloat(data.lng) };
    setActiveRoutes((prev) =>
      prev.map((r) =>
        r.id === data.routeId ? { ...r, lastLocationLat: pos.lat, lastLocationLng: pos.lng } : r,
      ),
    );
    setTrails((prev) => {
      const arr = prev[data.routeId] || [];
      const next = [...arr, pos];
      return { ...prev, [data.routeId]: next.length > MAX_TRAIL ? next.slice(-MAX_TRAIL) : next };
    });
  }, []);

  const handleFraudWarning = useCallback((data) => {
    toast.error(`⚠️ SHUBHALI HARAKAT: ${data.plateNumber} — ${data.message}`, {
      duration: 8000,
      style: { background: '#1a0a0a', border: '1px solid #ef4444', color: '#fca5a5' },
    });
    setFraudIds((prev) => new Set([...prev, data.vehicleId]));
    setTimeout(() => {
      setFraudIds((prev) => {
        const n = new Set(prev);
        n.delete(data.vehicleId);
        return n;
      });
    }, 30_000);
  }, []);

  useAdminSocket({
    onLocationUpdate: handleLocationUpdate,
    onFraudWarning: handleFraudWarning,
  });

  /* ─── Map center ─────────────────────────────────────── */
  const mapCenter = useMemo(() => {
    if (selected) {
      const r = activeRoutes.find(x => x.id === selected);
      if (r?.lastLocationLat) return { lat: parseFloat(r.lastLocationLat), lng: parseFloat(r.lastLocationLng) };
    }
    if (activeRoutes.length > 0 && activeRoutes[0].lastLocationLat) {
      return { lat: parseFloat(activeRoutes[0].lastLocationLat), lng: parseFloat(activeRoutes[0].lastLocationLng) };
    }
    return TASHKENT;
  }, [selected, activeRoutes]);

  /* ─── Pan map when selection changes ─────────────────── */
  useEffect(() => {
    if (mapRef.current && mapCenter) mapRef.current.panTo(mapCenter);
  }, [mapCenter]);

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <style>{`
        @keyframes ping  { 75%,100% { transform:scale(2); opacity:0; } }
        @keyframes pulse { 0%,100%  { opacity:1; } 50% { opacity:0.5; } }
      `}</style>

      <AdminPageHeader
        tag="LIVE TRACKING"
        title="Haydovchilarni kuzatish"
        subtitle={`${activeRoutes.length} haydovchi yo'nalishda`}
        icon="location_on"
        accent="rose"
      >
        {fraudIds.size > 0 ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-2xl px-4 py-2"
          >
            <span className="text-red-500 text-[10px] font-black uppercase tracking-widest animate-pulse">
              ⚠️ {fraudIds.size} FRAUD DETECTED
            </span>
          </motion.div>
        ) : null}
        <Button
          variant="ghost"
          onClick={fetchRoutes}
          className="h-14 w-14 rounded-3xl p-0 hover:bg-rose-500/10 hover:text-rose-500 transition-colors"
        >
          <span className="material-symbols-outlined">refresh</span>
        </Button>
      </AdminPageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        {/* ── Driver list ── */}
        <div className="xl:col-span-1 space-y-3 max-h-[700px] overflow-y-auto pr-1">
          {activeRoutes.length === 0 ? (
            <div className="text-center py-16 bg-slate-50/50 dark:bg-slate-900/30 rounded-[32px] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <span className="material-symbols-outlined text-4xl text-slate-300 block mb-3">directions_car_filled</span>
              <p className="text-[10px] font-black uppercase text-slate-400 italic">Faol haydovchilar yo'q</p>
            </div>
          ) : activeRoutes.map((route, idx) => {
            const color      = driverColor(idx);
            const isFraud    = route.vehicle && fraudIds.has(route.vehicle.id);
            const isSelected = selected === route.id;
            const lat        = route.lastLocationLat ? parseFloat(route.lastLocationLat) : null;
            const lng        = route.lastLocationLng ? parseFloat(route.lastLocationLng) : null;

            return (
              <motion.div
                key={route.id}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
                onClick={() => setSelected(isSelected ? null : route.id)}
                className="cursor-pointer"
              >
                <Card className={`p-4 rounded-3xl transition-all duration-200 ${
                  isSelected
                    ? 'border-2 shadow-xl dark:bg-slate-900'
                    : 'border-slate-100 dark:border-slate-800/50 hover:border-slate-200 dark:hover:border-slate-700 dark:bg-slate-900'
                }`}
                  style={isSelected ? { borderColor: color, boxShadow: `0 0 0 3px ${color}22` } : {}}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="h-11 w-11 rounded-2xl flex items-center justify-center overflow-hidden shadow-inner"
                        style={{ background: `${color}22`, border: `1.5px solid ${color}44` }}>
                        {route.driver?.avatarUrl
                          ? <img src={route.driver.avatarUrl} alt="" className="h-full w-full object-cover" />
                          : <span className="font-black text-sm" style={{ color }}>{(route.driver?.name || 'D')[0]}</span>
                        }
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black italic uppercase text-slate-900 dark:text-white truncate leading-tight">
                        {route.driver?.name || 'Driver'}
                      </div>
                      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                        {route.vehicle?.plateNumber || '—'}
                      </div>
                    </div>
                    {isFraud && (
                      <span className="text-red-500 animate-pulse text-base" title="Fraud detected">⚠️</span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-xs" style={{ color, fontSize: '14px' }}>location_on</span>
                    <span className="text-[9px] font-bold text-slate-500 truncate">
                      {lat ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : 'Aniqlanmoqda...'}
                    </span>
                  </div>

                  {isSelected && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                        {(trails[route.id]?.length || 0)} nuqta
                      </span>
                      <Badge variant="green" className="text-[7px] italic border-0 px-2 py-0.5">ONLINE</Badge>
                    </div>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* ── Map ── */}
        <div className="xl:col-span-3">
          <Card className="rounded-[40px] border-none overflow-hidden relative h-[700px] bg-slate-900 shadow-2xl">
            {/* Status badge */}
            <div className="absolute top-5 left-5 z-20">
              <div className="bg-black/60 backdrop-blur-xl p-3 rounded-2xl border border-white/10">
                <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 italic">
                  <span className="h-2 w-2 bg-emerald-400 rounded-full animate-ping" />
                  CONNECTED · {activeRoutes.length} drivers
                </p>
              </div>
            </div>

            {/* Legend */}
            {activeRoutes.length > 0 && (
              <div className="absolute top-5 right-5 z-20 space-y-1.5">
                {activeRoutes.map((r, idx) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-1.5 rounded-xl border border-white/10 cursor-pointer"
                    onClick={() => setSelected(selected === r.id ? null : r.id)}
                  >
                    <div className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: driverColor(idx) }} />
                    <span className="text-[8px] font-black text-white uppercase tracking-widest">
                      {r.driver?.name?.split(' ')[0] || 'Driver'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {!isGoogleMapsConfigured() ? (
              /* ── No API key fallback ── */
              <div className="h-full flex flex-col items-center justify-center p-10 text-center">
                <svg width="100%" height="100%" viewBox="0 0 100 100" className="opacity-10 absolute inset-0 pointer-events-none">
                  <defs>
                    <pattern id="agrid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M20 0L0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#agrid)" />
                </svg>
                <div className="relative z-10">
                  <span className="material-symbols-outlined text-[56px] text-slate-500 block mb-4">satellite_alt</span>
                  <h4 className="text-xl font-black italic uppercase text-white mb-2 tracking-tighter">Live GPS Tracking</h4>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 max-w-sm">
                    REACT_APP_GOOGLE_MAPS_API_KEY ni .env ga qo'shing
                  </p>
                  {/* Animated drivers as dots */}
                  <div className="relative mt-10 h-40 w-64 mx-auto">
                    {activeRoutes.map((route, i) => (
                      <motion.div
                        key={route.id}
                        className="absolute flex flex-col items-center"
                        initial={{ x: '40%', y: '40%' }}
                        animate={{ x: `${20 + i * 18}%`, y: `${25 + (i % 2) * 35}%` }}
                        transition={{ duration: 4 + i, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
                      >
                        <div className="h-4 w-4 rounded-full border-2 border-white/30 flex items-center justify-center"
                          style={{ background: driverColor(i), boxShadow: `0 0 12px ${driverColor(i)}88` }}>
                          <div className="h-1.5 w-1.5 bg-white rounded-full" />
                        </div>
                        <span className="text-[6px] text-white font-black mt-1 uppercase">
                          {route.driver?.name?.split(' ')[0]}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <AdminDriverGoogleMap
                activeRoutes={activeRoutes}
                trails={trails}
                fraudIds={fraudIds}
                selected={selected}
                setSelected={setSelected}
                mapCenter={mapCenter}
                mapOptions={mapOptions}
                mapRef={mapRef}
              />
            )}
          </Card>
        </div>
      </div>

      {/* ── Fraud alerts log ── */}
      <AnimatePresence>
        {fraudIds.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          >
            <Card className="p-6 rounded-3xl border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/10">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-red-500 material-symbols-outlined">warning</span>
                <h3 className="text-sm font-black uppercase tracking-widest text-red-600 dark:text-red-400">
                  Faol Fraud Ogohlantirishlari
                </h3>
              </div>
              <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">
                {fraudIds.size} ta transport vositasida shubhali harakat aniqlandi. Iltimos, darhol tekshiring.
              </p>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDriverTracking;
